import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCancelSession, useMarkAttendance, useSession } from "../../lib/queries";
import { Empty, ErrorBox, Loading } from "../../components/Loading";
import { useToast } from "../../components/Toast";
import { fmtDayLabel, fmtTimeRange } from "../../lib/format";
import { hapticImpact, setBackButton } from "../../lib/tg";

export default function SessionManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionQ = useSession(id);
  const cancelSession = useCancelSession();
  const markAttendance = useMarkAttendance();
  const toast = useToast();

  useEffect(() => setBackButton(() => navigate("/t")), [navigate]);

  if (sessionQ.isLoading) return <Loading />;
  if (sessionQ.error) return <ErrorBox message={(sessionQ.error as Error).message} />;
  if (!sessionQ.data) return <ErrorBox message="Не знайдено" />;

  const session = sessionQ.data;
  const at = session.activity_type;

  const bookings = session.bookings
    .filter((b) => b.status !== "cancelled")
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

  const skipassPending = bookings.filter((b) => b.needs_skipass);

  return (
    <div className="p-4 space-y-4">
      <header>
        <div className="text-tg-hint text-sm">{fmtDayLabel(session.starts_at)}</div>
        <h1 className="text-2xl font-bold mt-1">
          {at.emoji} {at.name}
        </h1>
        <div className="text-tg-hint mt-1">
          {fmtTimeRange(session.starts_at, session.ends_at)}
          {session.location_text && ` · ${session.location_text}`}
        </div>
        <div className="text-tg-hint text-sm mt-1">
          {bookings.length}/{session.capacity} записані · статус: {session.status}
        </div>
        {session.status === "published" && (
          <Link to={`/t/session/${session.id}/edit`} className="text-tg-link text-sm mt-2 inline-block">
            ✏️ Редагувати локацію
          </Link>
        )}
      </header>

      {skipassPending.length > 0 && (
        <section className="card border border-tg-link">
          <div className="font-semibold mb-1">🎫 Потрібен скіпас ({skipassPending.length})</div>
          <div className="text-sm">
            {skipassPending.map((b) => b.participant?.name ?? "?").join(", ")}
          </div>
          <button
            className="btn-secondary text-sm mt-2"
            onClick={() => {
              const text = skipassPending.map((b) => b.participant?.name ?? "?").join(", ");
              navigator.clipboard?.writeText(text);
              hapticImpact("light");
            }}
          >Скопіювати імена</button>
        </section>
      )}

      <section>
        <h2 className="font-semibold mb-2">Учасники</h2>
        {bookings.length === 0 ? (
          <Empty label="Ще немає записів." />
        ) : (
          <ul className="space-y-2">
            {bookings.map((b) => (
              <li key={b.id} className="card flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium">{b.participant?.name ?? "Учасник"}</div>
                  <div className="text-tg-hint text-sm">
                    {b.participant?.parent?.name && `(${b.participant.parent.name})`}
                    {b.needs_skipass && <span className="ml-2">🎫</span>}
                  </div>
                </div>
                <select
                  className="bg-tg-bg border border-tg-secondary rounded-xl px-2 py-1 text-sm"
                  value={b.status}
                  onChange={(e) => markAttendance.mutate(
                    { booking_id: b.id, status: e.target.value as typeof b.status },
                    { onError: (err) => toast.error((err as Error).message) },
                  )}
                >
                  <option value="confirmed">записаний</option>
                  <option value="attended">був</option>
                  <option value="noshow">не прийшов</option>
                </select>
              </li>
            ))}
          </ul>
        )}
      </section>

      {session.status === "published" && (
        <button
          className="btn-secondary w-full text-red-600"
          onClick={() => {
            if (confirm("Скасувати це заняття? Всі записи будуть позначені скасованими.")) {
              cancelSession.mutate(session.id, { onSuccess: () => navigate("/t") });
            }
          }}
        >Скасувати заняття</button>
      )}
    </div>
  );
}
